import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import { EVENT_TYPES } from '../utils/constants';
import { OPEN_USER_CHANNEL, CLOSE_USER_CHANNEL, UPDATE_USER } from '../actions/types';
import {
  updateUser,
  receiveManagedSoundcasts,
  openSoundcastChannel,
  closeSoundcastChannel,
  openPublisherChannel,
} from '../actions';
import { updateUserMetadata } from '../managers/user';
import { getUser, getPublisher, getManagedSoundcastById } from '../selectors';
import { createDynamicLiveChannel } from '../helpers/channels';

/**
 * Reads the `soundcasts_managed` of an admin user
 * and updates the `managed` reducer accordingly
 */
const userManagedChannelWorker = function* ({ payload: user }) {
  try {
    const { admin = false, soundcasts_managed: liveSoundcastsManaged = {} } = user;

    // do nothing its not an admin user
    if (!admin) {
      return;
    }

    // when modiying check old user redux
    // const liveSoundcastsManagedIds = Object.keys(liveSoundcastsManaged);
    const liveSoundcastsManagedIds = Object.entries(liveSoundcastsManaged)
      .filter(([, value]) => value !== false && value !== null)
      .map(([key]) => key);
    const liveSoundcastsManagedFixed = liveSoundcastsManagedIds.reduce(
      (prev, soundcastId) => {
        prev[soundcastId] = true;
        return prev;
      },
      {},
    );

    const localSoundcastsManagedByIds = yield select(getManagedSoundcastById);
    const localSoundcastsManagedIds = Object.keys(localSoundcastsManagedByIds);

    // if the live soundcasts_managed has a data in local
    // merge it to avoid rerender, let the soundcast updates itself
    const soundcastsManaged = liveSoundcastsManagedIds.reduce(
      (prev, soundcastId) => {
        if (!isEmpty(localSoundcastsManagedByIds) && soundcastId in localSoundcastsManagedByIds) {
          prev[soundcastId] = localSoundcastsManagedByIds[soundcastId];
        }

        return prev;
      },
      // { ...liveSoundcastsManaged }
      { ...liveSoundcastsManagedFixed }
    );

    // receive the managed soundcast (empty or not)
    // console.log('SoundcastsManaged new reducer', soundcastsManaged);
    yield put(receiveManagedSoundcasts(soundcastsManaged));

    // close existing channels for any local soundcast that were already opened
    if (!isEmpty(localSoundcastsManagedIds)) {
      // console.log('Soundcast managed LOCAL', localSoundcastsManagedIds);
      yield all(
        localSoundcastsManagedIds
          .reduce((prev, soundcastId) => {
            if (localSoundcastsManagedIds.includes(soundcastId)) {
              // console.log('closeSoundcastChannel', soundcastId);
              prev.push(closeSoundcastChannel(soundcastId));
            }

            return prev;
          }, [])
          .map(req => put(req))
      );
    }

    // open new live soundcasts
    if (!isEmpty(liveSoundcastsManagedIds)) {
      // console.log('Soundcast managed LIVE', liveSoundcastsManagedIds);
      yield all(
        liveSoundcastsManagedIds.map(soundcastId => {
          // console.log('openSoundcastChannel', soundcastId);
          return put(openSoundcastChannel(soundcastId));
        })
      );
    }
  } catch (error) {
    console.error(error);
    yield put(receiveManagedSoundcasts({}));
  }
};

const userManagedChannelWatcher = function* () {
  yield takeLatest(UPDATE_USER, userManagedChannelWorker);
};

const userHandler = function* (snapshot, userId) {
  try {
    if (snapshot.exists() && snapshot.hasChildren()) {
      const user = { ...snapshot.val() };

      // fix old users without the id prop
      if (!('id' in user)) {
        user.id = userId;
        yield call(updateUserMetadata, userId, { id: userId });
      }

      console.log('User from live channel', userId, user);
      yield put(updateUser(user));

      // if user is admin/publisher, open publishers channel for the first time
      const publisher = yield select(getPublisher);
      if (isEmpty(publisher)) {
        const { admin = false, publisherID } = yield select(getUser);
        if (admin) {
          // open publishers channel for the first time
          console.log('Opening publisher channel', publisherID);
          yield put(openPublisherChannel(publisherID));
        }
      }
    }
  } catch (error) {
    console.error(error);
  }
};

const userLiveChannelWorker = function* ({ payload: userId }) {
  yield call(createDynamicLiveChannel, {
    closeChannelAction: CLOSE_USER_CHANNEL,
    eventType: EVENT_TYPES.VALUE,
    handler: userHandler,
    pathToListen: `users/${userId}`,
    entity: userId,
    skipFirstRound: false,
  });
};

const userLiveChannelWatcher = function* () {
  yield takeLatest(OPEN_USER_CHANNEL, userLiveChannelWorker);
};

export default function* () {
  yield all([call(userLiveChannelWatcher), call(userManagedChannelWatcher)]);
}
